<template>
  <nx-fullscreen-dialog
    :active="active"
    :dismissable="dismissable"
    title="Alineación de fútbol"
    @dismiss="dismiss"
  >
    <template slot="toolbar-items">
      <v-btn
        @click.native="accept"
        color="accent"
        dark flat
        v-html="$t('message.common.accept')"
      ></v-btn>
    </template>


    <v-container pb-3 fluid>
      <v-flex class="md10 offset-md1 sm10 offset-sm1 pt-4">
        <div class="nx-live-football-field" ref="relWrapper" :class="{ loading }">
          <div class="field-wrapper" ref="fieldWrapper" draggable="false">
            <img :src="LiveFootballField" alt="Campo de fútbol" @load="initialize"
                 draggable="false">
          </div>
          <div class="bench-wrapper" ref="benchWrapper" draggable="false"></div>
          <div class="anti-ghosting"></div>
          <div
            v-for="player in compPlayers"
            :key="player.playerId"
            :style="player.style"
            class="player"
            :class="{ local: player.local, visiting: player.visiting, [player.playerId]: true }"
            @mousedown="onMouseDown(player)"
          >
            <div class="player__number" v-text="player.playerNumber"></div>
            <div class="player__name" v-text="player.playerName"></div>
          </div>
        </div>
      </v-flex>
    </v-container>


  </nx-fullscreen-dialog>
</template>

<script>
import NxFullscreenDialog from '@/components/dialogs/FullscreenDialog';
import LiveFootballField from '@/assets/live-football/football-field.svg';

const PLAYER_WIDTH = 90;
const PLAYER_HEIGHT = 40;
const HALF_PLAYER_WIDTH = PLAYER_WIDTH / 2;
const HALF_PLAYER_HEIGHT = PLAYER_HEIGHT / 2;

export default {
  name: 'nx-live-football-match-lineup',
  extends: NxFullscreenDialog,
  mixins: [],
  components: { NxFullscreenDialog },
  props: {
    title: {
      type: String,
    },
    players: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      LiveFootballField,

      relRect: {},
      fieldRect: {},
      benchRect: {},
      benchOverflow: 0,

      compPlayers: [],
      movingPlayer: null,

      loading: false,
    };
  },
  computed: {
    localTeamId() {
      return this.players.length
        ? this.players[0].teamId
        : null;
    },
  },
  methods: {
    // Helper functions to calculate different positions.
    /**
     * Returns the `top` property of a player from the `y` percentaje given by the server.
     */
    getTopFromPercentaje(y) {
      const pixel = (y || 0) * this.fieldRect.height / 100;
      return pixel - (HALF_PLAYER_HEIGHT / 2);
    },
    /**
     * Returns the `left` property of a player from the `x` percentaje given by the server.
     */
    getLeftFromPercentaje(x) {
      const pixel = (x || 0) * this.fieldRect.width / 100;
      return pixel - (HALF_PLAYER_WIDTH / 2);
    },

    /**
     * Returns the `x` percentaje to be saved, given the player's `top` pixels.
     */
    getYPercentajeFromPixel(top) {
      return (top + HALF_PLAYER_HEIGHT) * 100 / this.fieldRect.height;
    },
    /**
     * Returns the `y` percentaje to be saved, given the player's `left` pixels.
     */
    getXPercentajeFromPixel(left) {
      return (left + HALF_PLAYER_WIDTH) * 100 / this.fieldRect.width;
    },

    /**
     * Returns the player's `top` position, relative to the football field.
     */
    getRelativeTop(y) {
      return y - this.relRect.y;
    },
    /**
     * Returns the player's `left` position, relative to the football field.
     */
    getRelativeLeft(x) {
      return x - this.relRect.x;
    },

    /**
     * Returns the player's `top` position, relative to the bench div.
     */
    getBenchTop(y) {
      return this.getRelativeTop(this.benchRect.y + y);
    },
    /**
     * Returns the player's `left` position, relative to the bench div.
     */
    getBenchLeft(x) {
      return this.getRelativeLeft(this.benchRect.x + x);
    },

    /**
     * Returns the player's `top` position based on it's saved percentaje. If the percentaje is set,
     * it'll be transformed into pixels relative to the field. If the percentaje is not set, a new
     * position in the bench will be returned.
     */
    getPosTop({ y, inStartingEleven }, index) {
      if (y || inStartingEleven) return this.getTopFromPercentaje(y);
      return this.getBenchTop(5 + (index * PLAYER_HEIGHT));
    },
    /**
     * Returns the player's `left` position based on it's saved percentaje. If the percentaje is set,
     * it'll be transformed into pixels relative to the field. If the percentaje is not set, a new
     * position in the bench will be returned.
     */
    getPosLeft({ x, inStartingEleven }, index) {
      if (x || inStartingEleven) return this.getLeftFromPercentaje(x);
      return this.getBenchLeft(index * PLAYER_WIDTH);
    },

    // Kickstart
    initialize() {
      setTimeout(() => {
        this.relRect = this.$refs.relWrapper.getBoundingClientRect();
        this.fieldRect = this.$refs.fieldWrapper.getBoundingClientRect();
        this.benchRect = this.$refs.benchWrapper.getBoundingClientRect();
        this.initializePlayers();
        this.loading = false;
      }, 1000); // Wait till everithing is drawed to get the correct bounding rects.
    },
    initializePlayers() {
      const playerPerBenchRow = Math.floor(this.benchRect.width / PLAYER_WIDTH);
      this.compPlayers = this.players.map((player, index) => {
        const left = this.getPosLeft(player, index % playerPerBenchRow);
        const top = this.getPosTop(player, Math.floor(index / playerPerBenchRow));

        const local = player.teamId === this.localTeamId;
        return {
          ...player,
          local,
          visiting: !local,
          top,
          left,
          style: {
            top: `${top}px`,
            left: `${left}px`,
          },
        };
      }).reduce((acc, player) => {
        acc[player.playerId] = player;
        return acc;
      }, {});
    },

    /**
     * Accepts the current lineup. The players positions will be transformed to percentajes before
     * the input event it's emited.
     */
    accept() {
      const players = Object.values(this.compPlayers).map((compPlayer) => {
        const {
          local, visiting, top, left, style, ...player
        } = compPlayer;

        player.inStartingEleven = top < this.fieldRect.height;
        if (player.inStartingEleven) {
          player.x = this.getXPercentajeFromPixel(left);
          player.y = this.getYPercentajeFromPixel(top);
        } else {
          player.x = 0;
          player.y = 0;
        }
        return player;
      });
      this.$emit('input', players);
    },

    // Event handlers
    onMouseDown(player) {
      // When the user clicks in a player, a new event listener will be added to track the mouse movement.
      window.addEventListener('mousemove', this.onMouseMove, true);
      this.movingPlayer = player;
    },
    onMouseUp() {
      // When the mouse is released, remove the event listener.
      window.removeEventListener('mousemove', this.onMouseMove, true);
      this.movingPlayer = null;
    },
    onMouseMove(evt) {
      // While dragging a player, it'll be moved accordingly.
      if (this.movingPlayer) {
        const newX = evt.clientX - this.relRect.x - (PLAYER_WIDTH / 2);
        const newY = evt.clientY - this.relRect.y - (PLAYER_HEIGHT / 2);

        this.movingPlayer.top = Math.max(Math.min(this.relRect.height - PLAYER_HEIGHT, newY), 0);
        this.movingPlayer.left = Math.max(Math.min(this.relRect.width - PLAYER_WIDTH, newX), 0);
        this.movingPlayer.style.top = `${this.movingPlayer.top}px`;
        this.movingPlayer.style.left = `${this.movingPlayer.left}px`;
      }
    },
  },
  mounted() {
    this.loading = true; // Wait till everithing is drawed to get the correct bounding rects.
    window.addEventListener('mouseup', this.onMouseUp, true);
  },
  destroyed() {
    window.removeEventListener('mouseup', this.onMouseUp, true);
  },
};
</script>
<style lang="stylus" scoped>
.nx-live-football-field {
  max-width 900px
  margin: 0 auto
  position: relative
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -o-user-select: none;
  user-select: none;

  &.loading {
    visibility hidden
  }
}

.field-wrapper {
  position: relative;
  font-size: 0
}

.bench-wrapper {
  position: relative;
  height: 300px
  background-color: rgba(0, 0, 0, 0.2);

  &:before {
    color: white;
    content: 'Banquillo';
    display: block;
    font-family: monospace;
    font-size: 50px;
    opacity: .2;
    position: absolute;
    right: 50%;
    text-shadow: 0px 0px 7px black;
    text-transform: uppercase;
    top: 50%;
    transform: translate(50%, -50%);
  }
}

.anti-ghosting {
  bottom: 0
  height: 100%
  left: 0
  position: absolute;
  right: 0
  top: 0
  width: 100%
}

.player {
  cursor: move;
  display: block;
  position: absolute;
  text-align: center;
  width: 90px

  &__number {
    background: white;
    border-radius: 50%;
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.4);
    color black
    display: inline-block
    font-family: monospace;
    font-size: 14px;
    font-weight: bold;
    height: 23px;
    line-height: 23px;
    pointer-events none
    text-align: center;
    width: 23px;
  }

  &.local .player__number {
    color white
    background black
  }

  &__name {
    pointer-events none
    font-size: 11px
  }
}
</style>
